import { ColumnDef } from '@tanstack/react-table';
import { useDetailsBookingsVM } from './useDetailsBookingsVM';
import { MutationTable } from '@components/index';
import { BookingTable } from '@services/member/booking/booking.types';
import { UsePaginationMutation } from '@services/index';
import moment from 'moment';
import { Icon } from '@design-system/index';
import { Button } from '@design-system/index';
import { Pdf, ShopsSettingsLine, Warning } from '@assets/images/icons';
import { formatMoney } from '@utils/numbers';

interface DetailsBookingsProps {
    mutationHook: UsePaginationMutation<BookingTable>;
    /** Tri par défaut sur la date d'arrivée : 'asc' pour les listes à venir, 'desc' pour l'historique */
    defaultDirection?: 'asc' | 'desc';
    onBookingSelect: (booking: BookingTable) => void;
}

export default function DetailsBookings({ mutationHook, onBookingSelect, defaultDirection = 'desc' }: DetailsBookingsProps) {
    const { id, bookingColumnHelper } = useDetailsBookingsVM();

    const contributionsColumns = [
        bookingColumnHelper.accessor('member.cosemNumber', {
            header: 'N° COSEM',
            id: 'member.cosemNumber',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => (
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    {info.row.original.paymentInPending && (
                        <Icon icon={<Warning />} size={18} color="error" />
                    )}
                    <>{info.getValue()}</>
                </div>
            )
        }),
        bookingColumnHelper.accessor((row) => row.apartment?.label, {
            id: 'apartment.label',
            header: 'Logement',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()?.toUpperCase() ?? ''}</>
        }),
        bookingColumnHelper.accessor('requestDate', {
            header: 'Demandé le',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')} </>
        }),
        bookingColumnHelper.accessor((row) => row.rentalPrice, {
            id: 'rentalPrice',
            header: 'Prix',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{formatMoney(info.getValue())}</>
        }),
        bookingColumnHelper.accessor('arrivalDate', {
            header: 'Arrivée',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')} </>
        }),
        bookingColumnHelper.accessor('departureDate', {
            header: 'Départ',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')} </>
        }),
        bookingColumnHelper.display({
            id: 'action',
            size: 0,
            header: 'Documents',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        {info.row.original.confirmationPdfLink && (
                            <Button
                                variant="icon"
                                color="primary"
                                size="small"
                                icon={<Icon icon={<ShopsSettingsLine />} size={20} color="primary" />}
                                hoverIcon={<Icon icon={<ShopsSettingsLine />} size={20} color="secondary" />}
                                onClick={() => {
                                    window.open(info.row.original.confirmationPdfLink, '_blank');
                                }}
                            />
                        )}
                        {info.row.original.pdfLink && (
                            <Button
                                variant="icon"
                                color="primary"
                                size="small"
                                icon={<Icon icon={<Pdf />} size={20} color="primary" />}
                                hoverIcon={<Icon icon={<Pdf />} size={20} color="secondary" />}
                                onClick={() => {
                                    window.open(info.row.original.pdfLink, '_blank');
                                }}
                            />
                        )}
                    </div>
                );
            }
        })
    ] as ColumnDef<BookingTable>[];

    return (
        <MutationTable
            columns={contributionsColumns}
            mutationHook={mutationHook}
            mutationHookParams={{ id }}
            className="no-card"
            selectable={false} //TODO, false cause ui wrong alignment of pagination
            onRowClick={onBookingSelect}
            deleting={{
                enabled: true,
                entity: 'apartment_booking'
            }}
            defaultSorting={{
                field: 'arrivalDate',
                direction: defaultDirection
            }}
        />
    );
}
